import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";

const card = "rounded-2xl border border-adminborder bg-admincard";
const darkInput = "w-full rounded-lg border border-adminborder bg-adminbg px-3.5 py-2.5 text-sm text-slate-200 outline-none focus:border-saffron";

export default function AdminCommissions() {
  const [settings, setSettings] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/admin/commissions").then((r) => setCommissions(r.data)).catch(() => {});
  }, []);

  if (!settings) return <p className="py-20 text-center text-slate-500">Loading…</p>;

  const set = (k) => (e) => setSettings({ ...settings, [k]: parseFloat(e.target.value) || 0 });

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/admin/settings", settings);
      setSettings(res.data);
      toast.success("Pricing & commission settings saved");
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const Field = ({ label, field, suffix, testid }) => (
    <div>
      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</label>
      <div className="relative">
        <input type="number" step="0.1" min="0" data-testid={testid} className={darkInput}
               value={settings[field] ?? ""} onChange={set(field)} />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-500">{suffix}</span>
      </div>
    </div>
  );

  return (
    <div data-testid="admin-commissions" className="space-y-6">
      <h1 className="font-heading text-2xl font-bold tracking-tight text-white sm:text-3xl">Rates & Commissions</h1>

      <div className={`${card} p-6`}>
        <h3 className="font-heading text-lg font-bold text-white">Customer Pricing</h3>
        <p className="mt-1 text-xs text-slate-500">
          Customer Price = (Courier Base Rate × 125%) + Base Rate. Example: base ₹100 → customer pays ₹225.
        </p>
        <div className="mt-4 grid max-w-md gap-4">
          <Field label="Customer Markup over base courier rate" field="customer_markup_percent" suffix="%" testid="admin-margin-input" />
        </div>
      </div>

      <div className={`${card} p-6`} data-testid="volumetric-settings-card">
        <h3 className="font-heading text-lg font-bold text-white">Volumetric Weight Settings</h3>
        <p className="mt-1 text-xs text-slate-500">
          Used by the public volumetric calculator and pickup page. Volumetric kg = (L × W × H cm) ÷ divisor.
          Chargeable weight = higher of actual vs volumetric, rounded up.
        </p>
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <Field label="Domestic divisor" field="volumetric_divisor_domestic" suffix="cm³/kg" testid="admin-divisor-domestic" />
          <Field label="International divisor" field="volumetric_divisor_international" suffix="cm³/kg" testid="admin-divisor-international" />
          <Field label="Round chargeable up to nearest" field="weight_rounding_kg" suffix="kg" testid="admin-rounding-kg" />
        </div>
      </div>

      <div className={`${card} p-6`}>
        <h3 className="font-heading text-lg font-bold text-white">Franchise Commission (Domestic)</h3>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field label="Commission on shipment value" field="franchise_commission_percent" suffix="%" testid="franchise-commission-pct" />
          <Field label="Per-KG commission" field="franchise_per_kg_commission" suffix="₹/kg" testid="franchise-per-kg" />
        </div>
      </div>

      <div className={`${card} p-6`}>
        <h3 className="font-heading text-lg font-bold text-white">Agent Commission (Domestic)</h3>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field label="Commission on shipment value" field="agent_commission_percent" suffix="%" testid="agent-commission-pct" />
          <Field label="Per-lead commission" field="agent_per_lead_commission" suffix="₹/lead" testid="agent-per-lead" />
        </div>
      </div>

      <button onClick={save} disabled={saving} data-testid="save-pricing-btn"
              className="rounded-full bg-saffron px-7 py-3 text-sm font-bold text-white shadow-lg shadow-saffron/20 hover:bg-saffron-hover disabled:opacity-60">
        {saving ? "Saving…" : "Save Pricing Settings"}
      </button>

      <div className={`${card} overflow-x-auto`}>
        <h3 className="border-b border-adminborder px-5 py-4 font-heading text-lg font-bold text-white">Commission Ledger</h3>
        <table className="w-full text-sm" data-testid="commission-ledger-table">
          <thead>
            <tr className="border-b border-adminborder text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-5 py-3.5">Partner</th><th className="px-5 py-3.5">Role</th><th className="px-5 py-3.5">Order</th>
              <th className="px-5 py-3.5">Breakup</th><th className="px-5 py-3.5">Amount</th><th className="px-5 py-3.5">Status</th><th className="px-5 py-3.5">Date</th>
            </tr>
          </thead>
          <tbody>
            {commissions.map((c) => (
              <tr key={c.id} className="border-b border-adminborder/60 last:border-0">
                <td className="px-5 py-3.5">{c.user_name}<p className="text-xs text-slate-500">{c.user_email}</p></td>
                <td className="px-5 py-3.5 capitalize">{c.role}</td>
                <td className="px-5 py-3.5 font-mono text-xs text-saffron">{c.order_id}</td>
                <td className="px-5 py-3.5 text-xs text-slate-400">
                  {c.percent_amount != null && <span>%: ₹{c.percent_amount} </span>}
                  {c.per_kg_amount != null && <span>· per-kg: ₹{c.per_kg_amount}</span>}
                  {c.per_lead_amount != null && <span>· per-lead: ₹{c.per_lead_amount}</span>}
                </td>
                <td className="px-5 py-3.5 font-bold text-emerald-400">₹{c.amount}</td>
                <td className="px-5 py-3.5"><span className="rounded-full bg-amber-500/15 px-2.5 py-1 text-xs font-bold text-amber-400">{c.status}</span></td>
                <td className="px-5 py-3.5 text-xs text-slate-500">{new Date(c.created_at).toLocaleDateString("en-IN")}</td>
              </tr>
            ))}
            {!commissions.length && <tr><td colSpan={7} className="px-5 py-12 text-center text-slate-500">No commission entries yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
